import { cn } from "@-/com.npmjs.cn";
import type { MorphemeMnemonicsFlashcardSectionTabsGate } from "#get-morpheme-mnemonics-card-section-tabs-gate";
import { Pressable, View } from "@-/com.npmjs.react-native";
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "@-/com.npmjs.react-native-reanimated";
import { Portal } from "@-/com.npmjs.react-native-teleport";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { GraphemeFontGlyph } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.font";
import { GraphemeMorphemePanelsContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { useGraphemePanelsMnemonicsTabPress } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._context";
import {
  type MorphemeCardTabHost,
  MorphemeMnemonicsIntroducedReadingsContext,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels.tab-host-context";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@-/dev.improve.haniverse.components.ui.tooltip";
import { LearnCardBinderTabStrip } from "@-/dev.improve.haniverse.components.ui.learn-card-shell";
import { useLearnedPinyinSoundsQuery } from "@-/dev.improve.haniverse.lib.cards.user-card.grapheme.pronunciation.read";
import {
  type LearnedPinyinSounds,
  type PinyinSound,
  formatPinyinSoundName,
  getPinyinSounds,
  isPinyinSoundLearned,
  withLearnedPinyin,
} from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import { MorphemeTranscription } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.transcription";
import { usePinyinSoundButtonGate } from "@-/dev.improve.haniverse.routes.nav.app._components.pinyin-sound-button";
import { useGetGraphemeGlyph } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph/client";
import { useIsPronunciationPlaying } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.pinyin.audio";
import { useMorphemeTranscription } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.transcription";
import type { MorphemeTranscription as MorphemeTranscriptionValue } from "@-/dev.improve.haniverse.lib.morpheme.transcription.entities";
import { useContext, useEffect, useState, type ReactNode } from "react";

/** Every section shares the strip's width equally and centres its icon or pinyin. */
const SECTION_TAB_CLASS_NAME = "h-full min-w-0 flex-1 items-center justify-center";
const SECTION_TAB_CONTENT_CLASS_NAME = "flex-1 items-center justify-center self-stretch px-1";

function SectionTab({
  accessibilityLabel,
  children,
  isSelected,
  onPress,
  tabHost,
  testID,
}: {
  accessibilityLabel: string;
  children: ReactNode;
  isSelected: boolean;
  onPress: () => void;
  tabHost: MorphemeCardTabHost;
  testID: string;
}) {
  return (
    <Pressable
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="tab"
      accessibilityState={{ disabled: !tabHost.isInteractive, selected: isSelected }}
      className={SECTION_TAB_CLASS_NAME}
      disabled={!tabHost.isInteractive}
      onPress={onPress}
      testID={testID}
    >
      <View className={SECTION_TAB_CONTENT_CLASS_NAME} pointerEvents="none">
        {children}
      </View>
    </Pressable>
  );
}

/**
 * A reading's pinyin: tone-coloured once the reading is introduced, plain foreground while it is
 * not (its syllabus level is above the lesson's grade, or it is not in the learner's deck yet).
 * Muted grey would read as a disabled section, but a not-yet-introduced reading is still pressable.
 */
function ReadingPinyinText({
  className,
  isIntroduced,
  transcription,
}: {
  className: string;
  isIntroduced: boolean;
  transcription: MorphemeTranscriptionValue;
}) {
  return isIntroduced ? (
    <MorphemeTranscription className={className} numberOfLines={1} transcription={transcription} />
  ) : (
    <Text className={cn(className, "text-foreground")} numberOfLines={1}>
      {transcription.pinyin}
    </Text>
  );
}

/**
 * A reading's pinyin, which eases into bold (and full strength, when its section is not selected)
 * while the shared player speaks that reading — from a press or from the reveal's auto-play.
 * Font weight cannot be interpolated, so a bold copy cross-fades over the medium one; the bold
 * copy sizes the label so the swap never shifts it.
 */
function PlayingPinyinLabel({
  isIntroduced,
  isSelected,
  pinyinKey,
  transcription,
}: {
  isIntroduced: boolean;
  isSelected: boolean;
  pinyinKey: string;
  transcription: MorphemeTranscriptionValue;
}) {
  const isPlaying = useIsPronunciationPlaying(pinyinKey);
  const progress = useSharedValue(isPlaying ? 1 : 0);
  useEffect(() => {
    progress.set(
      withTiming(isPlaying ? 1 : 0, {
        duration: isPlaying ? 160 : 320,
        easing: Easing.inOut(Easing.cubic),
      }),
    );
  }, [isPlaying, progress]);
  const labelStyle = useAnimatedStyle(() => ({
    opacity: isSelected ? 1 : 0.6 + 0.4 * progress.get(),
  }));
  const boldStyle = useAnimatedStyle(() => ({ opacity: progress.get() }));
  const mediumStyle = useAnimatedStyle(() => ({ opacity: 1 - progress.get() }));
  return (
    <Animated.View className="max-w-full items-center justify-center" style={labelStyle}>
      <Animated.View style={boldStyle}>
        <ReadingPinyinText
          className="text-[13px] font-bold"
          isIntroduced={isIntroduced}
          transcription={transcription}
        />
      </Animated.View>
      <Animated.View className="absolute inset-0 items-center justify-center" style={mediumStyle}>
        <ReadingPinyinText
          className="text-[13px] font-medium"
          isIntroduced={isIntroduced}
          transcription={transcription}
        />
      </Animated.View>
    </Animated.View>
  );
}

/** One sound of a locked reading, and whether the learner has learned it yet. */
type ReadingSoundProgress = { isLearned: boolean; sound: PinyinSound };

/**
 * The sounds of a reading, when the learner cannot read it yet: some initial, final or tone of it
 * is not learned. `null` when every one is, which opens the reading's section.
 */
function getLockedReadingSounds(
  toneNumbersPinyin: string,
  learned: LearnedPinyinSounds,
): ReadingSoundProgress[] | null {
  const sounds = getPinyinSounds(toneNumbersPinyin).map((sound) => ({
    isLearned: isPinyinSoundLearned(sound, learned),
    sound,
  }));
  return sounds.every(({ isLearned }) => isLearned) ? null : sounds;
}

/**
 * A reading the learner cannot read yet: its pinyin in plain foreground, and a press that, instead
 * of switching to the reading and speaking it, explains what unlocks it — each of its sounds,
 * checked once learned and locked until then.
 */
function LockedReadingSectionTab({
  isSelected,
  lockedSounds,
  pinyin,
  pinyinKey,
  tabHost,
}: {
  isSelected: boolean;
  lockedSounds: readonly ReadingSoundProgress[];
  pinyin: string;
  pinyinKey: string;
  tabHost: MorphemeCardTabHost;
}) {
  const transcription = useMorphemeTranscription(pinyin);
  return (
    // The tooltip's root is the strip's flex item (it renders a view of its own); the trigger fills it.
    <Tooltip className={SECTION_TAB_CLASS_NAME} delayDuration={0}>
      <TooltipTrigger
        accessibilityHint="Shows the sounds to learn first"
        accessibilityLabel={`${pinyin}, locked`}
        accessibilityRole="tab"
        accessibilityState={{ disabled: !tabHost.isInteractive, selected: isSelected }}
        className="size-full items-center justify-center"
        disabled={!tabHost.isInteractive}
        testID={`learn-card-mnemonics-section-${pinyinKey}`}
      >
        <View className={SECTION_TAB_CONTENT_CLASS_NAME} pointerEvents="none">
          <PlayingPinyinLabel
            isIntroduced={false}
            isSelected={isSelected}
            pinyinKey={pinyinKey}
            transcription={transcription}
          />
        </View>
      </TooltipTrigger>
      <TooltipContent className="max-w-64" side="bottom">
        <View className="gap-1" testID={`learn-card-mnemonics-section-${pinyinKey}-locked`}>
          <Text className="font-semibold">Tab unlocks after learning:</Text>
          {lockedSounds.map(({ isLearned, sound }) => {
            const name = formatPinyinSoundName(sound);
            return (
              <View
                accessibilityLabel={`${name}, ${isLearned ? "learned" : "not learned yet"}`}
                accessible
                className="flex-row items-center justify-between gap-4"
                key={name}
              >
                <Text>{name}</Text>
                <Icon
                  className="text-white"
                  name={isLearned ? "MdCheck" : "MdLockOutline"}
                  size={14}
                />
              </View>
            );
          })}
        </View>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * A reading's section: its pinyin (tone-coloured once introduced), treated as an icon like the
 * native tabs. Pressing
 * it switches to the reading and speaks it — also when it is already selected. Whichever reading
 * is being spoken (a press, or a revealed card's auto-play) turns its pinyin bold.
 */
function ReadingSectionTab({
  isIntroduced,
  isSelected,
  navigate,
  pinyin,
  pinyinKey,
  tabHost,
}: {
  isIntroduced: boolean;
  isSelected: boolean;
  navigate: () => void;
  pinyin: string;
  pinyinKey: string;
  tabHost: MorphemeCardTabHost;
}) {
  const transcription = useMorphemeTranscription(pinyin);
  const [readonlySlice] = useContextSelectors(
    GraphemeMorphemePanelsContext,
    (c) => c.readonlySlice.data,
  );
  const soundGate = usePinyinSoundButtonGate(readonlySlice, pinyinKey);
  return (
    <SectionTab
      accessibilityLabel={isIntroduced ? pinyin : `${pinyin}, not introduced yet`}
      isSelected={isSelected}
      onPress={() => {
        navigate();
        void soundGate?.play();
      }}
      tabHost={tabHost}
      testID={`learn-card-mnemonics-section-${pinyinKey}`}
    >
      <PlayingPinyinLabel
        isIntroduced={isIntroduced}
        isSelected={isSelected}
        pinyinKey={pinyinKey}
        transcription={transcription}
      />
    </SectionTab>
  );
}

/** A reading's section, locked while the learner cannot read it (`lockedSounds`). */
function ReadingSection({
  isIntroduced,
  isSelected,
  lockedSounds,
  navigate,
  pinyin,
  pinyinKey,
  tabHost,
}: {
  isIntroduced: boolean;
  isSelected: boolean;
  lockedSounds: readonly ReadingSoundProgress[] | null;
  navigate: () => void;
  pinyin: string;
  pinyinKey: string;
  tabHost: MorphemeCardTabHost;
}) {
  return lockedSounds === null ? (
    <ReadingSectionTab
      isIntroduced={isIntroduced}
      isSelected={isSelected}
      navigate={navigate}
      pinyin={pinyin}
      pinyinKey={pinyinKey}
      tabHost={tabHost}
    />
  ) : (
    <LockedReadingSectionTab
      isSelected={isSelected}
      lockedSounds={lockedSounds}
      pinyin={pinyin}
      pinyinKey={pinyinKey}
      tabHost={tabHost}
    />
  );
}

/**
 * A Learn card's mnemonics sections, teleported into its Learn binder tab: the character's font
 * glyph (the meaning mnemonic, decomposition and the rest) followed by one section per reading,
 * shown as its pinyin — tone-coloured when the card treats the reading as introduced, plain
 * foreground when not (`MorphemeMnemonicsIntroducedReadingsContext`). Every section is an icon without a label, like the Details tab's section
 * buttons, and they mirror the browse page's native reading tabs. Only
 * `getMorphemeMnemonicsFlashcardSectionTabsGate` decides whether they show.
 *
 * A reading opens only once the learner can read it: every initial, final and tone of it learned
 * from a reading in study, or taught by this card (the readings it introduces). Until then its
 * section is locked (`LockedReadingSectionTab`).
 *
 * The character and every reading share the available Learn width equally. Details reserves
 * only its fixed icon width, independently of the labels or selected section.
 */
export function MorphemeMnemonicsFlashcardSectionTabs({
  activeIndex,
  gate,
  tabHost,
}: {
  activeIndex: number;
  gate: MorphemeMnemonicsFlashcardSectionTabsGate;
  tabHost: MorphemeCardTabHost;
}) {
  const { tabs } = gate;
  const [width, setWidth] = useState(0);
  const [front] = useContextSelectors(
    GraphemeMorphemePanelsContext,
    (c) => c.readonlySlice.data.grapheme,
  );
  // The character section is never tone-coloured; only the reading sections carry tone colours.
  const graphemeGlyph = useGetGraphemeGlyph()(front, { uncolored: true });
  const getTabPressHandler = useGraphemePanelsMnemonicsTabPress();
  const introducedPinyinSet = useContext(MorphemeMnemonicsIntroducedReadingsContext);
  // Unknown until the learner's cards load, when no reading is locked yet.
  const { data: learnedSounds } = useLearnedPinyinSoundsQuery();
  const cardLearnedSounds =
    learnedSounds === undefined || introducedPinyinSet === null
      ? undefined
      : withLearnedPinyin(learnedSounds, introducedPinyinSet);

  return (
    <Portal className="size-full" hostName={tabHost.hostName}>
      <View
        className="size-full flex-row"
        onLayout={({ nativeEvent: { layout } }) => {
          setWidth(layout.width);
        }}
      >
        {width > 0 ? (
          <LearnCardBinderTabStrip
            activeIndex={activeIndex}
            extendRight
            tabWidths={tabs.map(() => width / tabs.length)}
          />
        ) : null}
        {tabs.map((tab, index) => {
          const isSelected = index === activeIndex;
          return tab.pinyin === undefined ? (
            <SectionTab
              accessibilityLabel={`Character ${front}`}
              isSelected={isSelected}
              key={tab.name}
              onPress={getTabPressHandler(index)}
              tabHost={tabHost}
              testID="learn-card-mnemonics-section-meaning"
            >
              <GraphemeFontGlyph
                className={cn(
                  "text-[17px]",
                  isSelected ? "text-foreground" : "text-muted-foreground/70",
                )}
                glyph={graphemeGlyph}
              />
            </SectionTab>
          ) : (
            <ReadingSection
              isIntroduced={introducedPinyinSet?.has(tab.pinyin) ?? true}
              isSelected={isSelected}
              key={tab.name}
              lockedSounds={
                cardLearnedSounds === undefined
                  ? null
                  : getLockedReadingSounds(tab.pinyin, cardLearnedSounds)
              }
              navigate={getTabPressHandler(index)}
              pinyin={tab.title}
              pinyinKey={tab.pinyin}
              tabHost={tabHost}
            />
          );
        })}
      </View>
    </Portal>
  );
}
